import {ProductPrice, useProductOptions, Link} from '@shopify/hydrogen';
import clsx from 'clsx';
import type {ProductWithNodes, SanityProductPage} from '../../types';
import {hasMultipleProductOptions} from '../../utils/productOptions';
import SelectedVariantBuyNowButton from './buttons/SelectedVariantBuyNow.client';
import SelectedVariantAddToCartButton from './buttons/SelectedVariantAddToCart.client';
import ProductOptions from './options/ProductOptions.client';
import {MIN_CHECKOUT_PRICE} from '../../constants';

type Props = {
  sanityProduct: SanityProductPage;
  storefrontProduct: ProductWithNodes;
  emailInquire: string;
};

function ProductPrices({
  storefrontProduct,
}: {
  storefrontProduct: ProductWithNodes;
}) {
  const {selectedVariant} = useProductOptions();

  if (!storefrontProduct || !selectedVariant) {
    return null;
  }

  return (
    <div className="flex">
      <span
        className={clsx(
          'mr-fontSpace',
          selectedVariant.compareAtPriceV2 && 'text-main',
        )}
      >
        {selectedVariant.priceV2?.currencyCode}
      </span>
      <ProductPrice
        data={storefrontProduct}
        variantId={selectedVariant.id}
        className={clsx(
          selectedVariant.compareAtPriceV2 && 'mr-fontSpace text-main',
        )}
        withoutCurrency={true}
        withoutTrailingZeros={true}
      />
      <ProductPrice
        data={storefrontProduct}
        priceType="compareAt"
        variantId={selectedVariant.id}
        className={clsx(
          selectedVariant.compareAtPriceV2 && 'line-through decoration-main',
        )}
        withoutCurrency={true}
        withoutTrailingZeros={true}
      />
    </div>
  );
}

export default function ProductWidget({
  sanityProduct,
  storefrontProduct,
  emailInquire
}: Props) {
  const {selectedVariant, selectedOptions} = useProductOptions();
  const multipleProductOptions = hasMultipleProductOptions(
    storefrontProduct.options,
  );

  const availableForSale = selectedVariant?.availableForSale
  const quantityAvailable = selectedVariant?.quantityAvailable
  const isPOR = selectedVariant?.priceV2?.amount === '99999.0' ? true : false
  const price = selectedVariant?.priceV2?.amount

  if (!selectedVariant) {
    return null;
  }

  return (
    <div className={clsx(
      'WIDGET grid gap-5',
      'sm:grid-cols-2'
    )}>
      <div className="flex flex-col pr-20 text-small">
        <div className={clsx(
          // 'h-[85px]'
        )}>
          {/* Artist */}
          {sanityProduct?.artist && (
            <Link
              to={sanityProduct.artist.slug}
              className="w-fit hover:text-main"
            >
              {sanityProduct.artist.name}
            </Link>
          )}
          {/* Title */}
          {storefrontProduct?.title && (
            <h1 className={clsx(
              'text-gray',
              // 'truncate'
            )}>{storefrontProduct.title}</h1>
          )}
          {/* Prices */}
          {isPOR ? (
            <p className="text-main">Price on request</p>
          ) : availableForSale ? (
            <>
              <ProductPrices storefrontProduct={storefrontProduct} />
              {/* Sale */}
              {availableForSale && selectedVariant?.compareAtPriceV2 && (
                <div className="text-main">Sale</div>
              )}
              {sanityProduct.hideLastCopies !== true && quantityAvailable && quantityAvailable < 10 && (
                <p className="text-main">
                  {quantityAvailable === 1
                    ? '1 copy available'
                    : `${quantityAvailable} copies available`}
                </p>
              )}
            </>
          ) : (
            <p className="text-main">Sold out</p>
          )}
        </div>
        {/* Product actions */}
        <div className="mt-20 flex gap-5 pt-edit">
          {availableForSale && !isPOR ? (
            <>
              <SelectedVariantAddToCartButton />
              {price && parseInt(price) >= MIN_CHECKOUT_PRICE &&
                <SelectedVariantBuyNowButton />
              }
            </>
          ) : (
            <a
              href={selectedOptions && selectedOptions?.Title !== 'Default Title' ? (
                `mailto:${emailInquire}?Subject=${'Inquire: ' + storefrontProduct.title + " (" + Object.values(selectedOptions)[0] + ")" + ' by ' + sanityProduct?.artist?.name}&body=${'Hello, I would like to have more informations about ' + storefrontProduct.title + " (" + Object.values(selectedOptions)[0] + ")" + ' by ' + sanityProduct?.artist?.name + '.'}`
                ) : (
                `mailto:${emailInquire}?Subject=${'Inquire: ' + storefrontProduct.title + ' by ' + sanityProduct?.artist?.name}&body=${'Hello, I would like to have more informations about ' + storefrontProduct.title + ' by ' + sanityProduct?.artist?.name + '.'}`
              )
            }
              className={clsx(
                'w-fit text-gray border-gray',
                'border px-10 py-2 hover:bg-gray hover:text-white'
              )}
            >
              {selectedOptions &&  selectedOptions?.Title !== 'Default Title' ? (
                `Inquire (${Object.values(selectedOptions)[0]})`
              ) : (
                "Inquire"
              )}
            </a>
          )}
        </div>
      </div>
      {/* Product options */}
      {multipleProductOptions && (
        <ProductOptions
          // customProductOptions={sanityProduct.customProductOptions}
        />
      )}
    </div>
  );
}
